import { DocumentScannerRounded, Sync } from '@mui/icons-material';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Divider,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import { useFormik } from 'formik';
import React, { useEffect, useMemo, useState } from 'react';
import { useConfirm } from 'material-ui-confirm';
import SelectPlan from '../../../../components/SelectPlan';
import { PriceInputMasked } from '../../../../components/PriceMask';
import useErrors from '../../../../hooks/useErrors';
import { useFetch } from '../../../../hooks/useFecth';
import server from '../../../../services/server';
import { payment_status } from '../../../../services/helpers';
import { processAndSaveContractText } from '../../../../utils/processAndSaveContractText';

const PLAN_FUNCTION_LABELS = {
    cupons: 'Rede de descontos',
    vouchers: 'Rede local',
    cashback: 'Cashback',
    multi_app: 'App Seu Clube',
    custom_app: 'App Personalizado',
    reseller: 'Revenda/Página de vendas',
    benefit: 'Gestão de benefícios',
};

const PLAN_ADDITIONAL_LABELS = {
    telemedicina: 'Telemedicina',
    telepsicologia: 'Telepsicologia',
    seguro: 'Seguro de vida',
    telepet: 'Telepet',
};

function formatCurrency(value) {
    return Number(value ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function toKeyList(raw) {
    if (Array.isArray(raw)) return raw.filter(Boolean);
    if (typeof raw === 'string') {
        return raw
            .split(',')
            .map(s => s.trim())
            .filter(Boolean);
    }
    return raw ? [raw] : [];
}

export function Pagamento({ mutate, contract, title }) {
    const { setErrors, enqueueSnackbar } = useErrors();
    const confirm = useConfirm();
    const [isSyncing, setIsSyncing] = useState(false);

    const subscription = contract?.subscription;

    const { values, getFieldProps, setValues, setFieldValue, isSubmitting, handleBlur, errors, handleSubmit } = useFormik({
        initialValues: {
            planId: '',
            value: '',
            discount: '',
        },
        onSubmit: async (formValues, { setSubmitting }) => {
            try {
                setSubmitting(true);

                await confirm({
                    title: 'Alteração do Plano',
                    description: 'Tem certeza que deseja alterar o plano?',
                    confirmationText: 'Confirmo',
                });

                await server.put(`subscription/${contract.uuid}/plan`, {
                    planId: Number(formValues.planId),
                    value: Number(formValues.value),
                    discount: Number(formValues.discount) || 0,
                });
                await mutate();
                await processAndSaveContractText(contract.uuid, { mutate });
                enqueueSnackbar('Plano atualizado');
            } catch (error) {
                setErrors(error);
            } finally {
                setSubmitting(false);
            }
        },
    });

    const [selectedPlan, setSelectedPlan] = useState(null);

    const handlePlanChange = async ({ target: { value } }) => {
        try {
            await setFieldValue('planId', value, false);
            if (!value) {
                setSelectedPlan(null);
                await setFieldValue('value', '', false);
                return;
            }
            const { data } = await server.get(`plans/${value}`);
            const plan = data?.data || data;
            setSelectedPlan(plan || null);
            if (plan?.price != null) {
                await setFieldValue('value', plan.price, false);
            }
        } catch (error) {
            setErrors(error);
        }
    };

    useEffect(() => {
        if (!contract) return;

        setValues({
            planId: contract.planId ?? contract.plan?.id ?? '',
            value: contract.value ?? contract.plan?.price ?? '',
            discount: contract.discount ?? '',
        });
        if (contract.plan) {
            setSelectedPlan(contract.plan);
        }
    }, [contract, setValues]);

    const planPrice = useMemo(() => {
        return Number(
            values.value ??
                selectedPlan?.price ??
                contract?.value ??
                contract?.plan?.price ??
                0,
        );
    }, [values.value, selectedPlan?.price, contract?.value, contract?.plan?.price]);

    const appliedDiscount = Number(values.discount ?? contract?.discount ?? 0);
    const billableTotal = planPrice - appliedDiscount;

    const activePlanId = useMemo(
        () => selectedPlan?.id ?? values.planId ?? contract?.planId ?? contract?.plan?.id ?? null,
        [selectedPlan?.id, values.planId, contract?.planId, contract?.plan?.id],
    );

    const { data: priceTableData, isFetching: isLoadingPriceTable } = useFetch(
        activePlanId ? `plans/${activePlanId}/price-table` : null,
    );

    const priceTableRows = useMemo(() => {
        const rows = Array.isArray(priceTableData) ? priceTableData : [];
        return [...rows].sort((a, b) => Number(a.numberLife) - Number(b.numberLife));
    }, [priceTableData]);

    useEffect(() => {
        if (!contract || selectedPlan || !activePlanId) return;

        server
            .get(`plans/${activePlanId}`)
            .then(({ data }) => setSelectedPlan(data?.data || data))
            .catch(setErrors);
    }, [activePlanId, contract, selectedPlan, setErrors]);

    const paymentStatus = subscription?.paymentStatus;
    const paymentData = subscription?.paymentData;
    const canSyncPayment = Boolean(
        subscription?.paymentId || contract?.subscriptionAsaasId || subscription?.subscriptionId,
    );

    const handleSyncPayment = async () => {
        try {
            setIsSyncing(true);
            const { data } = await server.put(`subscription/${contract.uuid}/payment/sync`);
            await mutate();
            const status = data?.data?.paymentStatus;
            enqueueSnackbar(
                status
                    ? `Pagamento sincronizado: ${payment_status[status] || status}`
                    : 'Pagamento sincronizado com o Asaas.',
            );
        } catch (error) {
            setErrors(error);
        } finally {
            setIsSyncing(false);
        }
    };

    const waitingPayment = ['PENDING'].includes(paymentStatus);
    const paid = ['CONFIRMED', 'RECEIVED'].includes(paymentStatus);
    const notPaid = ['WAITING', 'PENDING', 'REPROVED_BY_RISK_ANALYSIS', 'AWAITING_RISK_ANALYSIS', 'ATTEMP', 'CREDIT_CARD_CAPTURE_REFUSED'].includes(
        paymentStatus,
    );
    const orderStatus = paymentStatus;

    const planFunctions = useMemo(
        () => toKeyList(selectedPlan?.functions ?? contract?.plan?.functions),
        [selectedPlan?.functions, contract?.plan?.functions],
    );
    const planAdditionals = useMemo(
        () => toKeyList(selectedPlan?.additionals ?? contract?.plan?.additionals),
        [selectedPlan?.additionals, contract?.plan?.additionals],
    );

    const planPriceTableSection = activePlanId ? (
        <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
                Faixa de preço por quantidade de vidas
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Valores de referência do plano conforme o número de vidas contratadas.
            </Typography>

            {isLoadingPriceTable ? (
                <Typography variant="body2" color="text.secondary">
                    Carregando faixas de preço...
                </Typography>
            ) : priceTableRows.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                    Este plano não possui faixas de preço cadastradas.
                </Typography>
            ) : (
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Até vidas</TableCell>
                                <TableCell align="right">Preço mensal</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {priceTableRows.map(row => (
                                <TableRow key={row.id}>
                                    <TableCell>{Number(row.numberLife)}</TableCell>
                                    <TableCell align="right">{formatCurrency(row.price)}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Paper>
    ) : null;

    return (
        <>
            <Stack spacing={2}>
                <>
                    <Typography variant="body2">* A cobrança será faturada em nome da empresa contrante que contra no contrato.</Typography>

                    <Stack direction="row" spacing={2}>
                        <SelectPlan
                            {...getFieldProps('planId')}
                            onChange={handlePlanChange}
                            valueSelected={selectedPlan?.id ?? contract?.planId}
                            fullWidth
                            error={!!errors.planId}
                            helperText={errors.planId}
                        />

                        <TextField
                            label="Valor do plano"
                            fullWidth
                            value={planPrice}
                            InputProps={{
                                readOnly: true,
                                inputComponent: PriceInputMasked,
                            }}
                        />
                    </Stack>

                    {(planFunctions.length > 0 || planAdditionals.length > 0) && (
                        <Stack spacing={1.5}>
                            {planFunctions.length > 0 && (
                                <Box>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                                        Funcionalidades do plano
                                    </Typography>
                                    <Stack direction="row" flexWrap="wrap" gap={0.75} useFlexGap>
                                        {planFunctions.map(key => (
                                            <Chip
                                                key={key}
                                                label={PLAN_FUNCTION_LABELS[key] || key}
                                                size="small"
                                                variant="outlined"
                                                color="primary"
                                            />
                                        ))}
                                    </Stack>
                                </Box>
                            )}
                            {planAdditionals.length > 0 && (
                                <Box>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                                        Benefícios do plano
                                    </Typography>
                                    <Stack direction="row" flexWrap="wrap" gap={0.75} useFlexGap>
                                        {planAdditionals.map(key => (
                                            <Chip
                                                key={key}
                                                label={PLAN_ADDITIONAL_LABELS[key] || key}
                                                size="small"
                                                variant="outlined"
                                                color="secondary"
                                            />
                                        ))}
                                    </Stack>
                                </Box>
                            )}
                        </Stack>
                    )}

                    {planPriceTableSection}

                    <TextField
                        value={planPrice}
                        label="Valor da mensalidade"
                        fullWidth
                        InputProps={{
                            readOnly: true,
                            inputComponent: PriceInputMasked,
                        }}
                    />

                    <>
                        <Stack direction="row" spacing={2}>
                            <TextField
                                label="Desconto aplicado"
                                {...getFieldProps('discount')}
                                fullWidth
                                disabled={!planPrice}
                                InputProps={{
                                    onBlur: handleBlur,
                                    inputComponent: PriceInputMasked,
                                    readOnly: paid,
                                }}
                            />
                        </Stack>

                        <Stack direction="row" spacing={2}>
                            <TextField
                                label="Valor a faturar"
                                fullWidth
                                value={billableTotal}
                                InputProps={{
                                    readOnly: true,
                                    inputComponent: PriceInputMasked,
                                }}
                            />
                        </Stack>
                    </>

                    {waitingPayment && (
                        <>
                            <Divider />
                            {paymentData && paymentData.billingType === 'BOLETO' && (
                                <Stack direction="row" spacing={2}>
                                    <Card>
                                        <CardContent>
                                            <Stack direction="row" columnGap={2} alignItems="center" justifyContent="center">
                                                <DocumentScannerRounded fontSize="large" sx={{ fontSize: 70 }} color="action" />
                                                <Divider orientation="vertical" flexItem />
                                                <Typography variant="body1" textAlign="center">
                                                    Seu boleto vence em ate 5 dias. Faça o pagamento para concluir a proposta.
                                                </Typography>
                                                <Divider orientation="vertical" flexItem />
                                                <Stack direction="row" sx={{ width: 200 }} justifyContent="center">
                                                    <Button variant="outlined" color="success" href={paymentData?.bankSlipUrl} target="_blank">
                                                        Ver Boleto
                                                    </Button>
                                                </Stack>
                                            </Stack>
                                        </CardContent>
                                    </Card>
                                </Stack>
                            )}

                            {paymentData && paymentData.billingType === 'PIX' && (
                                <Stack spacing={2} alignItems="center">
                                    <Box
                                        component="img"
                                        src={`data:image/png;base64,${paymentData?.chargePix?.encodedImage}`}
                                        alt="QrCode"
                                        width={200}
                                        height={200}
                                        border={1}
                                        borderColor="text.secondary"
                                        borderRadius={1}
                                    />
                                    <TextField
                                        fullWidth
                                        value={paymentData?.chargePix?.payload}
                                        label="Chave Pix"
                                        InputProps={{ readOnly: 'readonly' }}
                                    />
                                    <Button variant="outlined" target="_blank">
                                        Copiar codigo
                                    </Button>
                                </Stack>
                            )}
                        </>
                    )}

                    <Divider />
                    {paid && <Alert severity="success">Pagamento realizado com sucesso</Alert>}
                    {notPaid && (
                        <>
                            <Alert severity="info">{payment_status[orderStatus]}</Alert>
                            <Divider />
                        </>
                    )}
                    <Stack direction="row" spacing={1} justifyContent="flex-end" flexWrap="wrap">
                        <Button
                            variant="outlined"
                            startIcon={<Sync />}
                            onClick={handleSyncPayment}
                            disabled={isSyncing || isSubmitting || !canSyncPayment}
                            loading={isSyncing}
                        >
                            Sincronizar pagamento
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleSubmit}
                            disabled={isSubmitting || isSyncing}
                            loading={isSubmitting}
                            color="warning"
                        >
                            Atualizar
                        </Button>
                    </Stack>
                </>
            </Stack>
        </>
    );
}
